import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScrollReveal from "./ScrollReveal";

// --- Icons ---
const ShieldIcon = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 10a3 3 0 1 0 6 0" />
  </svg>
);

const ChipIcon = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
  >
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M9 9h6v6H9z" />
    <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
  </svg>
);

const ChartIcon = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
  >
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

// --- Data (Updated with your text) ---
const solutions = [
  {
    title: "Enterprise Cybersecurity",
    icon: <ShieldIcon />,
    description:
      "End-to-end cybersecurity for your business across your users, endpoints, and identity delivered by Synthera experts.",
    link: "/solutions/enterprise-cyber-security",
  },
  {
    title: "AI Security & Governance",
    icon: <ChipIcon />,
    description:
      "Control, secure, and govern your AI ecosystem GenAI, agents, and autonomous systems with Synthera.",
    link: "/solutions/ai-security-and-governance",
  },
  {
    title: "Operational Analytics",
    icon: <ChartIcon />,
    description:
      "Unlock the power of your data with deep analytics, visualization, and predictive insights for strategic growth.",
    link: "/solutions/data-analytics",
  },
];

// --- Card Component ---
const SolutionCard = ({ title, icon, description, link, index }) => {
  return (
    <motion.div
      className="relative h-full flex flex-col"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <Link to={link} className="block h-full no-underline">
        <div
          className="
            h-full min-h-[480px] px-8 py-12 flex flex-col items-center text-center
            /* FASTER TRANSITION: duration-200 */
            transition-all duration-200 ease-out
            
            /* --- STATE STYLING --- */
            bg-transparent
            
            /* Hover: Solid Dim Green Gradient */
            hover:bg-gradient-to-b hover:from-[#10b981] hover:to-[#047857]
            
            /* Shadow on hover */
            hover:shadow-2xl hover:shadow-emerald-900/50
          "
        >
          {/* Icon Section - MOVES UP ON HOVER */}
          <div className="mb-8 text-[#45d1b8] transition-all duration-300 group-hover:text-white transform group-hover:scale-110 group-hover:-translate-y-2">
            {icon}
          </div>

          {/* Title - MOVES UP ON HOVER */}
          <h3 className="text-2xl font-bold text-white mb-6 tracking-tight transition-transform duration-300 group-hover:-translate-y-2">
            {title}
          </h3>

          {/* Divider Line */}
          <div className="w-10 h-[2px] bg-emerald-500/50 mb-8 transition-colors duration-300 group-hover:bg-white/80"></div>

          {/* Description Text - MOVES DOWN ON HOVER (Expansion Effect) */}
          <div className="flex-grow flex flex-col items-center justify-start transition-transform duration-300 group-hover:translate-y-2">
            <p className="text-[16px] leading-relaxed text-emerald-100/70 group-hover:text-white transition-colors duration-200">
              {description}
            </p>
          </div>

          {/* "Learn More" Link - Pops up from bottom */}
          <div className="mt-8 opacity-0 translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
            <span className="text-white font-bold text-lg border-b-2 border-transparent group-hover:border-white pb-1">
              Learn more
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// --- Main Section ---
const SolutionsSection = () => {
  return (
    <ScrollReveal>
      <section
        className="py-24 px-4 font-sans text-white bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage:
            'url("https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1600/v1767026988/services_ony1hm.png")',
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#021a15]/85 pointer-events-none"></div>

        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header Text Area */}

          <div className="mb-14 px-4 text-center md:text-left">
            <p className="text-[#00C08B] font-bold uppercase tracking-widest text-xl mb-3">
              Our Solutions
            </p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Security Intelligence for Cloud and AI
            </h2>
            <p className="text-lg text-emerald-100/70 max-w-5xl leading-relaxed">
              Secure your enterprise, control AI risk and operational insights
              built for control, visibility, and scale.
            </p>
          </div>

          {/* THE BOX CONTAINER */}
          <div className="border border-emerald-500/20 rounded-sm bg-transparent backdrop-blur-[2px] shadow-2xl overflow-hidden flex flex-col">
            {/* Grid Area */}
            <div className="grid grid-cols-1 md:grid-cols-3 w-full">
              {solutions.map((solution, index) => (
                // The 'group' class here allows the card children to detect hover on this specific cell
                <div
                  key={index}
                  className="group border-r border-emerald-500/10 last:border-r-0"
                >
                  <SolutionCard {...solution} index={index} />
                </div>
              ))}
            </div>

            {/* Footer Area */}
            <div className="bg-[#022c22]/40 border-t border-emerald-500/10 py-6 text-center relative z-10">
              <p className="text-white font-medium text-lg tracking-wide opacity-80">
                Zero Trust Everywhere
              </p>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
};

export default SolutionsSection;
